import type { ModuleKey } from '@cloud/modules'

/**
 * Settings shell navigation — single source of truth for the settings
 * section in the main sidebar (the collapsible "Settings" group) and the
 * command menu. Item/group labels are full translation keys; groups with a
 * moduleKey are hidden when that module is disabled for the tenant.
 * `iconName` is a lucide icon name rendered via <DynamicIcon>.
 */
export interface SettingsNavItem {
  titleKey: string
  href: string
  iconName: string
  /** Alleen zichtbaar als deze feature van de module van de groep aan staat, zoals `featureKey`
   *  in `module-navigations.ts`. */
  featureKey?: string
}

export interface SettingsNavGroup {
  titleKey: string
  moduleKey?: ModuleKey
  items: SettingsNavItem[]
}

/**
 * De groepen die BOVENAAN blijven staan, in deze volgorde. Al het andere sorteert
 * alfabetisch op zijn vertaalde label (zie `settings-nav.tsx`), met een scheidingslijn
 * tussen de twee blokken.
 *
 * Waarom vastgezet en niet meegesorteerd: deze vier gaan over de hele TENANT in plaats van
 * over één module — bedrijfsgegevens, welke modules aan staan, de sleutels voor koppelingen,
 * en wie er bij mag. Ze alfabetisch laten meelopen zet CRM boven Algemeen, en dan begint het
 * instellingenmenu bij een willekeurige module. Toegang sluit het blok af omdat de rechten
 * pas betekenis krijgen als de rest is ingericht.
 */
export const PINNED_SETTINGS_GROUPS = [
  'navigation.general',
  'navigation.modules',
  'settings.nav.developer',
  'settings.nav.access',
] as const

export const settingsNavigationData: SettingsNavGroup[] = [
  {
    titleKey: 'navigation.general',
    items: [
      { titleKey: 'navigation.company', href: '/settings/company', iconName: 'Building2' },
      // Zonder nummer: de groep sorteert op het vertaalde label (settings-nav.tsx:62), dus
      // dit item komt per taal op zijn alfabetische plek; de genummerde items blijven 1 t/m 4.
      {
        titleKey: 'navigation.certifications',
        href: '/settings/certifications',
        iconName: 'BadgeCheck',
      },
      { titleKey: 'navigation.countries', href: '/settings/countries', iconName: 'Globe' },
      { titleKey: 'navigation.email', href: '/settings/email', iconName: 'Mail' },
      {
        titleKey: 'navigation.emailTemplates',
        href: '/settings/email-templates',
        iconName: 'MailPlus',
      },
      {
        titleKey: 'navigation.translations',
        href: '/settings/translations',
        iconName: 'Languages',
      },
      { titleKey: 'navigation.referenceLists', href: '/settings/reference', iconName: 'List' },
      // Verzendeenheden: core-stamdata sinds 2026-08-11, dus hier en niet meer onder
      // Packaging — CRM, de productmaster, de orderregel en het collo lezen ze alle vier.
      {
        titleKey: 'navigation.colloTypes',
        href: '/settings/collo-types',
        iconName: 'PackageOpen',
      },
      {
        titleKey: 'navigation.palletSizes',
        href: '/settings/pallet-sizes',
        iconName: 'Ruler',
      },
      { titleKey: 'navigation.shiftTypes', href: '/settings/shift-types', iconName: 'Clock' },
      {
        titleKey: 'navigation.workCalendar',
        href: '/settings/work-calendar',
        iconName: 'CalendarClock',
      },
      { titleKey: 'navigation.calendar', href: '/settings/calendar', iconName: 'CalendarDays' },
      { titleKey: 'navigation.numbering', href: '/settings/numbering', iconName: 'Hash' },
      { titleKey: 'navigation.quotas', href: '/settings/quotas', iconName: 'Gauge' },
    ],
  },
  {
    titleKey: 'settings.nav.access',
    items: [
      { titleKey: 'navigation.users', href: '/settings/users', iconName: 'Users' },
      { titleKey: 'navigation.teams', href: '/settings/teams', iconName: 'UsersRound' },
      {
        titleKey: 'navigation.rolesPermissions',
        href: '/settings/roles',
        iconName: 'ShieldCheck',
      },
      // Het overzicht van alle gebruikers met hun rollen. Zoals de andere ingangen hier zonder
      // eigen rechtencontrole in het menu: het scherm vraagt `rbac.user_roles:read`.
      { titleKey: 'rbac.roles.assignTitle', href: '/settings/roles/users', iconName: 'UserCog' },
    ],
  },
  {
    titleKey: 'navigation.modules',
    items: [
      { titleKey: 'navigation.modules', href: '/settings/modules', iconName: 'Boxes' },
      { titleKey: 'navigation.marketplace', href: '/settings/marketplace', iconName: 'Store' },
      {
        titleKey: 'navigation.connectedServices',
        href: '/settings/connected-services',
        iconName: 'Plug',
      },
    ],
  },
  {
    titleKey: 'navigation.crm',
    moduleKey: 'crm',
    items: [
      { titleKey: 'navigation.pipelines', href: '/settings/crm/pipelines', iconName: 'GitBranch' },
      { titleKey: 'navigation.webhooks', href: '/settings/crm/webhooks', iconName: 'Webhook' },
      {
        titleKey: 'navigation.currencySettings',
        href: '/settings/crm/currency-settings',
        iconName: 'Coins',
      },
      {
        titleKey: 'navigation.currencyRates',
        href: '/settings/crm/currency-rates',
        iconName: 'ArrowLeftRight',
      },
      { titleKey: 'navigation.lookups', href: '/settings/crm/lookups', iconName: 'Tags' },
      { titleKey: 'navigation.visitors', href: '/settings/crm/visitors', iconName: 'DoorOpen' },
    ],
  },
  {
    titleKey: 'navigation.hr',
    moduleKey: 'hr',
    items: [
      {
        titleKey: 'navigation.timeRegistrationSettings',
        href: '/settings/hr/time-registration',
        iconName: 'Clock',
      },
      {
        titleKey: 'navigation.leaveTypes',
        href: '/settings/hr/leave-types',
        iconName: 'CalendarOff',
      },
    ],
  },
  {
    titleKey: 'navigation.finance',
    moduleKey: 'finance',
    items: [
      { titleKey: 'navigation.taxRates', href: '/settings/finance/tax-rates', iconName: 'Percent' },
      {
        titleKey: 'navigation.taxNotes',
        href: '/settings/finance/tax-notes',
        iconName: 'ScrollText',
      },
      {
        titleKey: 'navigation.paymentTerms',
        href: '/settings/finance/payment-terms',
        iconName: 'CalendarClock',
      },
      {
        titleKey: 'navigation.approval',
        href: '/settings/finance/approval',
        iconName: 'CheckCheck',
      },
      { titleKey: 'navigation.dunning', href: '/settings/finance/dunning', iconName: 'BellRing' },
      {
        titleKey: 'navigation.autoInvoicing',
        href: '/settings/finance/auto-invoicing',
        iconName: 'Zap',
      },
      {
        titleKey: 'navigation.credit',
        href: '/settings/finance/credit',
        iconName: 'ShieldCheck',
        featureKey: 'credit_management',
      },
    ],
  },
  {
    titleKey: 'navigation.inventory',
    moduleKey: 'inventory',
    items: [
      {
        titleKey: 'navigation.locations',
        href: '/settings/inventory/locations',
        // lucide-icoonnaam, geen domeinwoord — lucide kent geen 'Location'.
        iconName: 'Warehouse',
      },
      {
        titleKey: 'navigation.webhooks',
        href: '/settings/inventory/webhooks',
        iconName: 'Webhook',
      },
    ],
  },
  {
    titleKey: 'navigation.packaging',
    moduleKey: 'packaging',
    items: [
      { titleKey: 'navigation.setup', href: '/settings/packaging', iconName: 'Wrench' },
      {
        titleKey: 'navigation.bundleStackings',
        href: '/settings/packaging/bundle-stackings',
        iconName: 'SquareStack',
      },
    ],
  },
  {
    titleKey: 'navigation.logistics',
    moduleKey: 'logistics',
    items: [
      {
        titleKey: 'navigation.deliveryMethods',
        href: '/settings/logistics/delivery-methods',
        iconName: 'Truck',
      },
      {
        titleKey: 'navigation.logisticsSettings',
        href: '/settings/logistics',
        iconName: 'Wrench',
      },
    ],
  },
  {
    // De groep draagt een moduleKey, dus hij verdwijnt vanzelf bij een tenant zonder PPWR —
    // net als logistiek hierboven. De TABELLEN bestaan wel altijd (base-spoor); dit gaat over
    // het scherm.
    titleKey: 'navigation.ppwr',
    moduleKey: 'ppwr',
    items: [
      {
        titleKey: 'navigation.ppwrSettings',
        href: '/settings/ppwr',
        iconName: 'FileCheck',
      },
    ],
  },
  {
    titleKey: 'settings.nav.developer',
    items: [
      { titleKey: 'navigation.apiKeys', href: '/settings/api-keys', iconName: 'KeyRound' },
      {
        titleKey: 'navigation.apiKeysAudit',
        href: '/settings/api-keys/audit',
        iconName: 'KeySquare',
      },
      {
        titleKey: 'navigation.oidcClients',
        href: '/settings/oidc-clients',
        iconName: 'FingerprintPattern',
      },
      {
        titleKey: 'navigation.integrationConnections',
        href: '/settings/integrations/connections',
        iconName: 'Cable',
      },
      {
        titleKey: 'navigation.integrationTasks',
        href: '/settings/integrations/tasks',
        iconName: 'ListChecks',
      },
      {
        titleKey: 'navigation.integrationRuns',
        href: '/settings/integrations/runs',
        iconName: 'ScrollText',
      },
      {
        titleKey: 'navigation.integrationDatasets',
        href: '/settings/integrations/datasets',
        iconName: 'Table2',
      },
    ],
  },
]
